import React, { useState, useMemo, useCallback } from "react";
import { useQuery } from "@apollo/client";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import RefreshIcon from "@mui/icons-material/Refresh";
import { usePermitApplicationContext } from "./PermitApplicationContext";
import { PermitTypeCard, PermitTypeCardSkeleton, PermitTypeWithZones } from "./components/PermitTypeCard";
import { ZoneMapDialog } from "@app.automotus.io/components/permits/ZoneMapDialog";
import {
  GET_PERMIT_TYPES_FOR_PAYEE,
  GetPermitTypesForPayeeData,
  GetPermitTypesForPayeeVars,
  transformPermitType,
} from "common/graphql/queries/GetPermitTypesForPayee";

const SKELETON_COUNT = 4;

export const SelectPermitType: React.FC = () => {
  const { state, setPermitType } = usePermitApplicationContext();
  const { authorityId } = state.authority;
  const selectedPermitTypeId = state.permitTypeSelection.permitTypeId;

  const [zoneDialogOpen, setZoneDialogOpen] = useState(false);
  const [selectedForMap, setSelectedForMap] = useState<PermitTypeWithZones | null>(null);

  const { data, loading, error, refetch } = useQuery<GetPermitTypesForPayeeData, GetPermitTypesForPayeeVars>(
    GET_PERMIT_TYPES_FOR_PAYEE,
    {
      variables: { payeeAccountId: authorityId || "" },
      skip: !authorityId,
      fetchPolicy: "cache-and-network",
    },
  );

  const permitTypes = useMemo(
    () => (data?.permitTypes || []).map((raw) => transformPermitType(raw) as PermitTypeWithZones),
    [data],
  );

  const handleSelect = useCallback(
    (permitType: PermitTypeWithZones) => {
      setPermitType(permitType.id, permitType);
    },
    [setPermitType],
  );

  const handleViewZones = useCallback((permitType: PermitTypeWithZones) => {
    setSelectedForMap(permitType);
    setZoneDialogOpen(true);
  }, []);

  const handleCloseZoneDialog = useCallback(() => {
    setZoneDialogOpen(false);
    setSelectedForMap(null);
  }, []);

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  // No authority selected state
  if (!authorityId) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Authority Selected
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please go back and select an authority first.
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={handleRetry}>
              Retry
            </Button>
          }
        >
          Failed to load permit types. Please try again.
        </Alert>
      </Box>
    );
  }

  // Loading state
  if (loading && !data) {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight={600}>
            Select Permit Type
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Choose the permit that best fits your operational needs
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
            <Grid item xs={12} md={6} key={index}>
              <PermitTypeCardSkeleton />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // Empty state
  if (permitTypes.length === 0) {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight={600}>
            Select Permit Type
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Choose the permit that best fits your operational needs
          </Typography>
        </Box>
        <Box
          sx={{
            border: 1,
            borderStyle: "dashed",
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "action.hover",
            py: 8,
            textAlign: "center",
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No permits are currently available for this authority.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please select a different authority or check back later.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Main content
  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" component="h2" gutterBottom fontWeight={600}>
          Select Permit Type
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Choose the permit that best fits your operational needs
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {permitTypes.map((permitType) => (
          <Grid item xs={12} md={6} key={permitType.id}>
            <PermitTypeCard
              permitType={permitType}
              isSelected={permitType.id === selectedPermitTypeId}
              onSelect={handleSelect}
              onViewZones={handleViewZones}
            />
          </Grid>
        ))}
      </Grid>

      {/* Zone Map Dialog */}
      <ZoneMapDialog
        open={zoneDialogOpen}
        onClose={handleCloseZoneDialog}
        title={selectedForMap ? `${selectedForMap.name} - Coverage Zones` : "Coverage Zones"}
        zones={selectedForMap?.zones || []}
      />
    </Box>
  );
};

export default SelectPermitType;
